import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Barcode from 'react-barcode';

import {
  Card,
  Badge,
  Button,
  Select,
  Row,
  Col,
  Icon,
} from '@folio/stripes/components';

const UserFriendlyPreview = ({ items, settings }) => {
  const intl = useIntl();
  const previewRef = useRef(null);
  const [viewMode, setViewMode] = useState('actual'); // actual, scaled, ruler
  const [selectedItem, setSelectedItem] = useState(0);

  /**
   * Get real-world measurements based on printer type
   */
  const getRealMeasurements = () => {
    const { printerType = 'ZEBRA_ZPL' } = settings;

    // Real-world label sizes in mm
    const labelSizes = {
      ZEBRA_ZPL: {
        width: 101.6,  // 4 inches = 101.6mm
        height: 50.8,  // 2 inches = 50.8mm
        name: '4" × 2"'
      },
      ESC_POS: {
        width: 57.15,  // 2.25 inches = 57.15mm
        height: 38.1,  // 1.5 inches = 38.1mm
        name: '2.25" × 1.5"'
      },
      BROTHER_PTOUCH: {
        width: 25.4,   // 1 inch = 25.4mm
        height: 12.7,  // 0.5 inch = 12.7mm
        name: '1" × 0.5"'
      }
    };

    return labelSizes[printerType] || labelSizes.ZEBRA_ZPL;
  };

  /**
   * Convert mm to pixels for actual size display
   * Using standard 96 DPI for screens
   */
  const mmToPixels = (mm) => {
    const dpi = 96; // Standard screen DPI
    const inches = mm / 25.4;
    return Math.round(inches * dpi);
  };

  /**
   * Generate realistic barcode preview
   */
  const generateRealisticBarcode = (item) => {
    const measurements = getRealMeasurements();
    const widthPx = mmToPixels(measurements.width);
    const heightPx = mmToPixels(measurements.height);

    const barcodeOptions = {
      value: item.barcode,
      format: settings.format || 'CODE128',
      width: 2,
      height: Math.round(heightPx * 0.4), // 40% of label height
      displayValue: settings.includeText,
      fontSize: Math.round(heightPx * 0.08), // 8% of label height
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000'
    };

    return { barcodeOptions, widthPx, heightPx };
  };

  /**
   * Create ruler for size reference
   */
  const createRuler = () => {
    const measurements = getRealMeasurements();
    const widthPx = mmToPixels(measurements.width);

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '8px',
        fontSize: '10px',
        color: '#666'
      }}
      >
        <div style={{
          width: widthPx,
          height: '10px',
          background: 'linear-gradient(90deg, #000 0px, #000 1px, transparent 1px, transparent 5px)',
          backgroundSize: '5px 100%',
          border: '1px solid #000',
          borderTop: 'none'
        }}
        />
        <span style={{ marginLeft: '8px' }}>
          {measurements.name} ({measurements.width}mm × {measurements.height}mm)
        </span>
      </div>
    );
  };

  const viewModeOptions = [
    {
      value: 'actual',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.userPreview.mode.actual',
        defaultMessage: 'Actual Size (1:1)'
      })
    },
    {
      value: 'scaled',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.userPreview.mode.scaled',
        defaultMessage: 'Fit to Screen'
      })
    },
    {
      value: 'ruler',
      label: intl.formatMessage({
        id: 'ui-plugin-inventory-templates.barcodePrinting.userPreview.mode.ruler',
        defaultMessage: 'With Ruler'
      })
    }
  ];

  if (items.length === 0) {
    return null;
  }

  const currentItem = items[selectedItem] || items[0];
  const { barcodeOptions, widthPx, heightPx } = generateRealisticBarcode(currentItem);
  const measurements = getRealMeasurements();

  // Scale factor for different view modes
  const getScaleFactor = () => {
    switch (viewMode) {
      case 'scaled':
        return Math.min(300 / widthPx, 200 / heightPx, 1);
      case 'actual':
      case 'ruler':
      default:
        return 1;
    }
  };

  const scaleFactor = getScaleFactor();
  const displayWidth = widthPx * scaleFactor;
  const displayHeight = heightPx * scaleFactor;

  return (
    <Card>
      <div style={{ padding: '16px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h4 style={{ margin: 0, marginRight: '8px' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.userPreview.title"
                defaultMessage="Print Preview"
              />
            </h4>
            <Badge color="primary">
              <Icon icon="eye-open" size="small" />
            </Badge>
          </div>

          <Select
            dataOptions={viewModeOptions}
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            marginBottom0
          />
        </div>

        {/* Item Selector for multiple items */}
        {items.length > 1 && (
          <div style={{ marginBottom: '16px' }}>
            <Row>
              <Col xs={6}>
                <strong>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.barcodePrinting.userPreview.previewItem"
                    defaultMessage="Preview Item:"
                  />
                </strong>
              </Col>
              <Col xs={6}>
                <Select
                  dataOptions={items.map((item, index) => ({
                    value: index,
                    label: `${index + 1}. ${item.barcode}`
                  }))}
                  value={selectedItem}
                  onChange={(e) => setSelectedItem(parseInt(e.target.value, 10))}
                  marginBottom0
                  fullWidth
                />
              </Col>
            </Row>
          </div>
        )}

        {/* Size Information */}
        <div style={{
          backgroundColor: '#e8f4fd',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px',
          fontSize: '12px'
        }}
        >
          <Row>
            <Col xs={4}>
              <strong>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodePrinting.userPreview.labelSize"
                  defaultMessage="Label Size:"
                />
              </strong>
              <div>{measurements.name}</div>
            </Col>
            <Col xs={4}>
              <strong>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodePrinting.userPreview.printer"
                  defaultMessage="Printer:"
                />
              </strong>
              <div>{settings.printerType || 'ZEBRA_ZPL'}</div>
            </Col>
            <Col xs={4}>
              <strong>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.barcodePrinting.userPreview.totalLabels"
                  defaultMessage="Total Labels:"
                />
              </strong>
              <div>{items.length} × {settings.copies} = {items.length * settings.copies}</div>
            </Col>
          </Row>
        </div>

        {/* Ruler (if enabled) */}
        {viewMode === 'ruler' && createRuler()}

        {/* Main Preview */}
        <div style={{
          textAlign: 'center',
          padding: '20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '4px',
          border: '2px dashed #ddd'
        }}
        >

          {viewMode === 'actual' && (
            <div style={{
              backgroundColor: '#fffacd',
              padding: '8px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '12px',
              color: '#856404'
            }}
            >
              <Icon icon="info" size="small" style={{ marginRight: '4px' }} />
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.userPreview.actualSizeNote"
                defaultMessage="This is the actual size - place a ruler on your screen to verify!"
              />
            </div>
          )}

          {/* Label Container */}
          <div
            ref={previewRef}
            style={{
              width: `${displayWidth}px`,
              height: `${displayHeight}px`,
              border: '1px solid #000',
              backgroundColor: '#ffffff',
              margin: '0 auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '4px',
              boxSizing: 'border-box'
            }}
          >
            {/* Barcode */}
            <div style={{ transform: `scale(${scaleFactor})`, transformOrigin: 'center' }}>
              <Barcode
                {...barcodeOptions}
                height={barcodeOptions.height * scaleFactor}
                fontSize={barcodeOptions.fontSize * scaleFactor}
              />
            </div>

            {/* Additional Item Info */}
            {settings.includeText && (
              <div style={{
                fontSize: `${8 * scaleFactor}px`,
                color: '#000',
                textAlign: 'center',
                marginTop: '2px',
                lineHeight: 1.2
              }}
              >
                <div>{currentItem.materialType?.name || 'N/A'}</div>
                <div>{currentItem.effectiveLocation?.name || 'N/A'}</div>
              </div>
            )}
          </div>

          {/* Size Indicators */}
          <div style={{
            marginTop: '16px',
            fontSize: '11px',
            color: '#666',
            display: 'flex',
            justifyContent: 'center',
            gap: '20px'
          }}
          >
            <span>Width: {measurements.width}mm</span>
            <span>Height: {measurements.height}mm</span>
            {viewMode !== 'actual' && (
              <span>Scale: {Math.round(scaleFactor * 100)}%</span>
            )}
          </div>
        </div>

        {/* Copy Information */}
        {settings.copies > 1 && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '4px',
            marginTop: '16px',
            textAlign: 'center',
            fontSize: '12px'
          }}
          >
            <Icon icon="duplicate" size="small" style={{ marginRight: '4px' }} />
            <FormattedMessage
              id="ui-plugin-inventory-templates.barcodePrinting.userPreview.copiesNote"
              defaultMessage="Each item will print {copies} times"
              values={{ copies: settings.copies }}
            />
          </div>
        )}

        {/* Print Tips */}
        <div style={{
          backgroundColor: '#d1ecf1',
          padding: '12px',
          borderRadius: '4px',
          marginTop: '16px',
          fontSize: '11px'
        }}
        >
          <strong>
            <Icon icon="lightbulb" size="small" style={{ marginRight: '4px' }} />
            <FormattedMessage
              id="ui-plugin-inventory-templates.barcodePrinting.userPreview.tips"
              defaultMessage="Tips:"
            />
          </strong>
          <ul style={{ margin: '4px 0 0 16px', paddingLeft: '0' }}>
            <li>
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.userPreview.tip1"
                defaultMessage="Verify barcode size with a physical ruler"
              />
            </li>
            <li>
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.userPreview.tip2"
                defaultMessage="Test print one label before printing all"
              />
            </li>
            <li>
              <FormattedMessage
                id="ui-plugin-inventory-templates.barcodePrinting.userPreview.tip3"
                defaultMessage="Check that text is readable at this size"
              />
            </li>
          </ul>
        </div>
      </div>
    </Card>
  );
};

UserFriendlyPreview.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  settings: PropTypes.shape({
    format: PropTypes.string.isRequired,
    includeText: PropTypes.bool.isRequired,
    copies: PropTypes.number.isRequired,
    printerType: PropTypes.string,
  }).isRequired,
};

export default UserFriendlyPreview;
